import { action, computed } from 'mobx';

import { ViewController } from '@estee/elc-service';

import { OTP_FLOW } from '~constants/Constants';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { ForgotPasswordUseCase } from '~use-cases/ForgotPasswordUseCase';
import { LoginSubmittedUseCase } from '~use-cases/LoginSubmittedUseCase';
import { getHideSocialPositionWrapper } from '~utils/SocialUtils';

import { View } from '../../domain/entities/SignInWithOtpFormStore';

interface IAccountSignInFormViewController {
    userRepository: UserRepository;
    configRepository: ConfigRepository;
    messageRepository: MessageRepository;
}
export class AccountSignInFormViewController extends ViewController {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private messageRepository: MessageRepository;

    constructor({
        userRepository,
        configRepository,
        messageRepository
    }: IAccountSignInFormViewController) {
        super();

        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.messageRepository = messageRepository;
    }

    @computed
    public get data() {
        const { accountSignInFormStore, isSignInToConnect } = this.userRepository;
        const { socialProviders, iamConfig, excludedFromSocialLogin } = this.configRepository;
        const infoMessages = this.messageRepository.messages.get(
            MessageGroup.SIGN_IN_SOCIAL_CONNECTION
        );
        const errorMessages = this.messageRepository.messages.get(MessageGroup.SIGN_IN);
        const hideSocialButtons =
            excludedFromSocialLogin ||
            getHideSocialPositionWrapper(socialProviders?.providers, isSignInToConnect);

        return {
            messages: this.messageRepository.messages,
            infoMessages,
            errorMessages,
            formStore: accountSignInFormStore,
            socialConfig: socialProviders,
            additionalTermsLabelKey: iamConfig?.additionalTermsLabelKey,
            hideSocialButtons
        };
    }

    @action
    public signInButtonClicked = async () => {
        // TODO: add support for mobile number sign in
        const {
            accountSignInFormStore,
            accountSignInFormStore: {
                validateForm,
                isValid,
                values: { accountIdentifier, password }
            }
        } = this.userRepository;

        validateForm();
        isValid &&
            (await new LoginSubmittedUseCase({
                signInData: { accountIdentifier, password },
                formStore: accountSignInFormStore
            }).execute());
    };

    @action
    public forgotPasswordClicked = () => {
        new ForgotPasswordUseCase().execute();
    };

    @action
    public resetPasswordClicked = () => {
        const { setOtpFlow, setView } = this.userRepository.signInWithOtpFormStore;

        setOtpFlow(OTP_FLOW.RESET_PASSWORD);
        setView(View.EMAIL);
    };

    @action
    public emailMeCodeToSignInClicked = () => {
        const { setOtpFlow, setView } = this.userRepository.signInWithOtpFormStore;

        setOtpFlow(OTP_FLOW.SIGN_IN_WITH_OTP);
        setView(View.EMAIL);
    };
}
